import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../redux/userRedux';
import { isSessionValid, getToken, getTokenTimeRemaining } from '../utils/tokenUtils';
import { toast } from 'react-toastify';

/**
 * Custom hook to monitor token expiry and auto-logout
 * Checks token validity on mount and periodically
 */
const useTokenExpiry = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);
  const checkInterval = useRef(null);
  const warningShown = useRef(false);

  useEffect(() => {
    // Only run if user is logged in
    if (!user) {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
        checkInterval.current = null;
      }
      return;
    }

    // Check token validity immediately
    if (!isSessionValid(user)) {
      console.log('Session expired on mount');
      toast.error('Your session has expired. Please login again.');
      dispatch(logout());
      return;
    }

    // Set up periodic check (every minute)
    checkInterval.current = setInterval(() => {
      if (!isSessionValid(user)) {
        console.log('Session expired');
        toast.error('Your session has expired. Please login again.');
        dispatch(logout());
        clearInterval(checkInterval.current);
        checkInterval.current = null;
      } else {
        // Check if token is expiring soon (within 5 minutes)
        const token = getToken(user);
        const timeRemaining = getTokenTimeRemaining(token);
        
        if (timeRemaining && timeRemaining < 300 && !warningShown.current) {
          // Show warning only once
          warningShown.current = true;
          toast.warning('Your session will expire soon. Please save your work.');
        }
      }
    }, 60000); // Check every minute

    // Cleanup on unmount
    return () => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
        checkInterval.current = null;
      }
    };
  }, [user, dispatch]);

  return null;
};

export default useTokenExpiry;
